/**
 * copyToClipboard — small, testable clipboard helper (Push 5 / v1.4.3 b5).
 *
 * The Dex diagnostics "Copy JSON" button needs a single place to:
 *   - call the async Clipboard API when it exists (web + iOS WKWebView with
 *     a secure context),
 *   - fall back to the legacy execCommand("copy") path used by older
 *     WebViews, and
 *   - report a boolean result so the UI can show success/failure feedback
 *     without throwing into the diagnostics render.
 *
 * Returns true on a successful copy, false otherwise. Never throws — callers
 * surface the boolean as toast/status text.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Secure-context / permission failure — fall through to execCommand.
    }
  }

  // Legacy fallback for WebViews without the async Clipboard API.
  if (typeof document !== "undefined" && typeof document.execCommand === "function") {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }

  return false;
}
